/**
 * API Service for ClientDashboard
 */

import {
  ApiContext,
  ApprovalStatusRow,
  DashboardFiltersPayload,
  OfficeStatusRow,
  PartnerStatusRow,
  ClientPracticeSummaryRow,
  LineOfServiceSummaryRow,
  ClientTypeSummaryRow,
  PrimaryPartnerClientTypeRow,
  OfficeClientTypeRow,
} from "./ClientDashboard.utils";

// Final combined response shape returned to dashboard component
// after all parallel API calls complete
export type DashboardDataBundle = {
  approvalStatus: ApprovalStatusRow[];
  office: OfficeStatusRow[];
  primaryPartner: PartnerStatusRow[];
  summary: ClientPracticeSummaryRow[];
  lineOfService: LineOfServiceSummaryRow[];
  clientType: ClientTypeSummaryRow[];
  primaryPartnerClientType: PrimaryPartnerClientTypeRow[];
  officeClientType: OfficeClientTypeRow[];
};

// Builds request headers for authenticated API calls
// Includes bearer token from local storage
const withAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("token") || "";

  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
};

// Standard JSON response handler
// - handles session expiry
// - parses JSON safely
// - throws readable API errors
const handleJson = async <T>(response: Response): Promise<ApiContext<T>> => {
  // Handle expired / invalid session
  if (response.status === 401) {
    alert("Session ended, please log in again");
    return {
      success: false,
      message: "Unauthorized",
      data: undefined,
    };
  }

  let json: any = null;

  // Safely parse JSON response
  try {
    json = await response.json();
  } catch {
    // no-op
  }

  // Throw backend error if request failed
  if (!response.ok) {
    const msg =
      json?.message ||
      json?.error ||
      response.statusText ||
      "Request failed.";

    throw new Error(msg);
  }

  return (json || {}) as ApiContext<T>;
};

// Cleans and normalizes filter payload before API call
// Key logic:
// - remove empty arrays
// - remove "All"
// - trim values
// - stringify arrays for backend SP consumption
const normalizeFiltersPayload = (
  payload: DashboardFiltersPayload | null | undefined
): Record<string, string | number> => {
  // If payload is invalid, send empty object
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }

  // Cleans array filters and converts them into JSON strings
  const cleanAndStringify = (arr?: string[]) => {
    if (!Array.isArray(arr)) return undefined;

    const cleaned = arr
      .map((s) => (typeof s === "string" ? s.trim() : ""))
      .filter(Boolean)
      .filter((s) => s.toLowerCase() !== "all");

    return cleaned.length ? JSON.stringify(cleaned) : undefined;
  };

  const normalized: Record<string, string | number> = {};

  // Normalize each filter field
  const bu = cleanAndStringify(payload.BusinessUnit);
  const office = cleanAndStringify(payload.Office);
  const partner = cleanAndStringify(payload.PrimaryPartner);
  const fy = cleanAndStringify(payload.FiscalYear);

  // Only include valid filters
  if (bu) normalized.BusinessUnit = bu;
  if (office) normalized.Office = office;
  if (partner) normalized.PrimaryPartner = partner;
  if (fy) normalized.FiscalYear = fy;

  return normalized;
};

// Shared POST helper used by all dashboard endpoints
// Applies auth headers + normalized payload + response handling
const post = async <T>(
  url: string,
  payload?: DashboardFiltersPayload | null
): Promise<ApiContext<T>> => {
  const safePayload = normalizeFiltersPayload(payload);

  const response = await fetch(url, {
    method: "POST",
    headers: withAuthHeaders(),
    body: JSON.stringify(safePayload),
  });

  return handleJson<T>(response);
};

// Dashboard API endpoints
const endpoints = {
  dashboardAll: "/mdm/clients/dashboard/client_dashboard_all/",
};

// Main dashboard data fetch
// Runs all API calls in parallel for faster dashboard load
export const fetchDashboardData = async (
  baseUri: string,
  filtersPayload?: DashboardFiltersPayload | null
): Promise<DashboardDataBundle> => {
  const base = (baseUri || "").replace(/\/$/, "");

  const res = await post<DashboardDataBundle>(
    `${base}${endpoints.dashboardAll}`,
    filtersPayload
  );

  if (res.errors && Object.keys(res.errors).length > 0) {
    console.warn("Client Dashboard section errors:", res.errors);
  }

  return {
    approvalStatus: res.data?.approvalStatus || [],
    office: res.data?.office || [],
    primaryPartner: res.data?.primaryPartner || [],
    summary: res.data?.summary || [],
    lineOfService: res.data?.lineOfService || [],
    clientType: res.data?.clientType || [],
    primaryPartnerClientType: res.data?.primaryPartnerClientType || [],
    officeClientType: res.data?.officeClientType || [],
  };
};